/**
 * useOfflineSync Hook
 *
 * Auto-syncs pending offline inspections when the device comes back online.
 * Uploads each inspection from IndexedDB to the server, links to workpack items,
 * and removes from IndexedDB on success.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  getInspections,
  deleteInspection,
  clearFileBackup,
  type OfflineInspection,
} from '../services/offlineEEHAStorage';
import * as eehaApi from '../services/eehaApi';

interface SyncProgress {
  total: number;
  synced: number;
  failed: number;
  warned: number;
  warnings: Array<{ tagNo: string; warnings: string[] }>;
  isSyncing: boolean;
}

export function useOfflineSync(isOnline: boolean): {
  syncProgress: SyncProgress;
  pendingCount: number;
  syncNow: () => Promise<void>;
} {
  const [pendingCount, setPendingCount] = useState(0);
  const [syncProgress, setSyncProgress] = useState<SyncProgress>({
    total: 0,
    synced: 0,
    failed: 0,
    warned: 0,
    warnings: [],
    isSyncing: false,
  });
  const syncingRef = useRef(false);

  // Refresh pending count
  const refreshCount = useCallback(async () => {
    try {
      const inspections = await getInspections();
      setPendingCount(inspections.length);
    } catch {
      // Ignore
    }
  }, []);

  // Sync a single inspection to the server
  const syncOne = async (inspection: OfflineInspection): Promise<{ success: boolean; warnings?: string[] }> => {
    try {
      const { localId, timestamp: _timestamp, offlineMode: _offlineMode, photos: _photos, lastModified: _lastModified, ...inspectionData } = inspection;

      // Create inspection on server
      const created = await eehaApi.createInspection(inspectionData as any);

      // Link to workpack item if applicable
      const wpId = (inspection as any).workpackId;
      const tagId = (inspection as any).tagId;
      if (wpId && tagId && created._id) {
        try {
          await eehaApi.updateWorkpackItem(wpId, tagId, {
            inspectionId: created._id,
            result: (inspection as any).overallResult || undefined,
            status: 'Completed',
          });
        } catch (linkErr) {
          console.error('[OfflineSync] Failed to link inspection to workpack:', linkErr);
        }
      }

      // Remove from IndexedDB and OPFS backup on success
      if (localId) {
        await deleteInspection(localId);
        clearFileBackup(localId).catch(() => {});
      }

      return { success: true, warnings: created._warnings };
    } catch (err) {
      console.error('[OfflineSync] Failed to sync inspection:', err);
      return { success: false };
    }
  };

  // Main sync function
  const syncNow = useCallback(async () => {
    if (syncingRef.current || !isOnline) return;
    syncingRef.current = true;

    try {
      const inspections = await getInspections();
      if (inspections.length === 0) {
        syncingRef.current = false;
        return;
      }

      setSyncProgress({
        total: inspections.length,
        synced: 0,
        failed: 0,
        warned: 0,
        warnings: [],
        isSyncing: true,
      });

      let synced = 0;
      let failed = 0;
      let warned = 0;
      const allWarnings: Array<{ tagNo: string; warnings: string[] }> = [];

      for (const inspection of inspections) {
        const result = await syncOne(inspection);
        if (result.success) {
          synced++;
          if (result.warnings && result.warnings.length > 0) {
            warned++;
            allWarnings.push({
              tagNo: (inspection as any).tagNo || 'Unknown',
              warnings: result.warnings,
            });
          }
        } else {
          failed++;
        }
        setSyncProgress({
          total: inspections.length,
          synced,
          failed,
          warned,
          warnings: allWarnings,
          isSyncing: true,
        });
      }

      setSyncProgress({
        total: inspections.length,
        synced,
        failed,
        warned,
        warnings: allWarnings,
        isSyncing: false,
      });

      console.info(`[OfflineSync] Complete: ${synced} synced, ${failed} failed`);

      // Refresh pending count
      await refreshCount();
    } catch (err) {
      console.error('[OfflineSync] Sync error:', err);
      setSyncProgress((prev) => ({ ...prev, isSyncing: false }));
    } finally {
      syncingRef.current = false;
    }
  }, [isOnline, refreshCount]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline) {
      // Small delay to let network stabilize
      const timer = setTimeout(() => {
        syncNow();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, syncNow]);

  // Check pending count on mount and when online status changes
  useEffect(() => {
    refreshCount();
  }, [refreshCount, isOnline]);

  return { syncProgress, pendingCount, syncNow };
}
